import React from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import WorkoutHistoryModal from "pages/WorkoutsPage/Vet/WorkoutsTable/WorkoutRow/WorkoutHistoryModal";
import TableCellWrapper from "../../TableCellWrapper/TableCellWrapper";
import {
  ERowColumn,
  IExamsTableRowData,
  ITableColumnsContent
} from "../../types";
import AssignedVetCell from "./AssignedVetCell";
import CsaCell from "./CsaCell/CsaCell";
import DaysSinceEventCell from "./DaysSinceEventCell/DaysSinceEventCell";
import LastExamCell from "./LastExamCell/LastExamCell";
import RiskFactorsCell from "./RiskFactorsCell/RiskFactorsCell";
import RiskLevelCell from "./RiskLevelCell/RiskLevelCell";
import VetListCell from "./VetListCell/VetListCell";

import useStyles from "./styles";

interface IProps {
  rowData: IExamsTableRowData;
  columns: string[];
  facilityBarns: Pick<racehorse360.IBarn, "id" | "name">[];
  selectedFacilityId: string;
  customColumnsContent?: ITableColumnsContent;
  bold?: boolean;
  extenderFitsTwoColumns?: boolean;
  extenderFitsOneColumns?: boolean;
}

const RowContent = (props: IProps) => {
  const {
    columns,
    rowData,
    selectedFacilityId,
    customColumnsContent = {},
    facilityBarns,
    bold = false,
    extenderFitsTwoColumns = false,
    extenderFitsOneColumns = false
  } = props;
  const {
    eventId,
    eventDate,
    pastEventsCount,
    horse,
    highSpeedFurlongs,
    examAssignedUser
  } = rowData;

  const classes = useStyles();

  const baseColumnsContent: ITableColumnsContent = {
    [ERowColumn.HISTORY]: (
      <TableCellWrapper key={`${eventId}-history`} centered>
        {Boolean(pastEventsCount) && (
          <WorkoutHistoryModal horse={horse} sourceDate={eventDate} />
        )}
      </TableCellWrapper>
    ),
    [ERowColumn.RISK_LEVEL]: (
      <RiskLevelCell
        key={`${eventId}-risk-level`}
        bold={bold}
        healthWatchStatus={horse.healthWatchStatus}
      />
    ),
    [ERowColumn.CSA]: (
      <CsaCell key={`${eventId}-csa`} bold={bold} horse={horse} />
    ),
    [ERowColumn.HSF]: (
      <TableCellWrapper key={`${eventId}-hsf`} centered bold={bold}>
        {highSpeedFurlongs}
      </TableCellWrapper>
    ),
    [ERowColumn.DLW]: (
      <DaysSinceEventCell
        key={`${eventId}-dlw`}
        bold={bold}
        lastEventDate={horse.lastWorkoutDate}
      />
    ),
    [ERowColumn.DLR]: (
      <DaysSinceEventCell
        key={`${eventId}-dlr`}
        bold={bold}
        lastEventDate={horse.lastRaceDate}
      />
    ),
    [ERowColumn.LAST_EXAM]: (
      <LastExamCell key={`${eventId}-last-exam`} bold={bold} horse={horse} />
    ),
    [ERowColumn.ACTIVE_VET_LISTS]: (
      <VetListCell key={`${eventId}-active-vet-lists`} horse={horse} />
    ),
    [ERowColumn.RISK_FACTORS]: (
      <RiskFactorsCell
        key={`${eventId}-risk-factors`}
        riskFactorShortDescriptions={horse.riskFactorShortDescriptions}
        horseName={horse.name}
        horseId={horse.id}
      />
    ),
    [ERowColumn.ASSIGNED_VET]: (
      <AssignedVetCell
        key={`${eventId}-assigned-vet`}
        assignedVet={examAssignedUser}
      />
    ),
    [ERowColumn.EXTENDER]: (
      <TableCellWrapper
        key={`${eventId}-extender`}
        lastTwoColumnsCell={extenderFitsTwoColumns}
        lastOneColumnCell={extenderFitsOneColumns}
      >
        &nbsp;
      </TableCellWrapper>
    )
  };

  const allColumnsContent: ITableColumnsContent = {
    ...baseColumnsContent,
    ...customColumnsContent
  };

  return columns.map(column => allColumnsContent[column]);
};

export default React.memo(RowContent);
